import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';

import { environment } from 'src/environments/environment';
import { global_variables } from 'src/environments/global_variables';
import { HttpService } from '../services/http.service';
import { AuthService } from '../services/auth.service';
import { ZoneService } from '../zone-report/zone.service';
import { SensorSchema } from '../helpers/sensor-schema';
import { SensorsDashboardData } from '../sensors-dashboard/sensors-dashboard-data';

type StatusCategory = 'online' | 'running' | 'offline';
type FreshnessTier = 'fresh' | 'warm' | 'cold';
type StaffFilter = 'all' | 'active' | 'inactive';
type SortKey =
   | 'name'
   | 'name-desc'
   | 'ping-newest'
   | 'ping-oldest'
   | 'live-first'
   | 'offline-first'
   | 'running-first'
   | 'stopped-first'
   | 'company';

// Freshness of the latest run carrying analytical data.
//   < 3 hours  → fresh  (reporting normally)
//   < 30 days  → warm   (stale, worth checking)
//   ≥ 30 days  → cold   (effectively dead)
//   never ran  → cold   (no analytical data at all)
const FRESHNESS_FRESH_MAX_MIN = 180;    // < 3 hours
const FRESHNESS_WARM_MAX_MIN  = 43200;  // < 30 days
const AMBIENT_RELOAD_DEBOUNCE_MS = 1500;

const STATUS_LABELS: Record<StatusCategory, string> = {
   online:  'Idle',      // reachable, but not part-way through a run
   running: 'Running',
   offline: 'Offline',
};

/** Same glyphs the landing-page KPI cards use. */
const STATUS_ICONS: Record<StatusCategory, string> = {
   online:  'fiber_manual_record',
   running: 'play_circle_filled',
   offline: 'cancel',
};

interface Cycle {
   current: number;
   total: number;
   percent: number | null;
}

interface Wind {
   speed: number | null;
   direction: number | null;
   runTimeLocal: string | null;
   loading?: boolean;
}

/** Live interior telemetry decoded from SensorData/status/<key>/recent/value. */
interface Interior {
   temp: number | null;
   humidity: number | null;
   firmware: string | null;
}

/**
 * Status-row label that carries the firmware VERSION. These types also expose a
 * "Firmware Date" row, so exclude dates — otherwise whichever row happens to come
 * first in the array wins and the chip shows "07222026" instead of "3.4.2".
 */
const FIRMWARE_RE = /(firm|fw)/i;
const FIRMWARE_EXCLUDE_RE = /date/i;

/** Coded field ids for a sensor type, resolved from its status rows. */
interface TypeFields {
   tempId?: string;
   humId?: string;
   fwId?: string;
}

interface BoardSensor {
   key: string;
   name: string;
   address: string;
   customerName: string;
   customerId: string;
   sensorTypeId: string;
   timezone: string;
   isActive: boolean;
   statusCategory: StatusCategory;
   freshnessTier: FreshnessTier;
   timeElapsed: string;
   lastReportUtc: string;
   /** lastReportUtc rendered in the sensor's own timezone. */
   lastReportLocal: string;
   minutesElapsed: number;
   cycle: Cycle | null;
   wind: Wind | null;
   interior: Interior | null;
   vitals: {
      ambientTemp: number | null;
      ambientHumidity: number | null;
      ambientPressure: number | null;
   };
   searchBlob: string;
}

/**
 * Per-client "All sensors" board.
 *
 * Live (RTDB subscriptions):
 *  - status (online/running/offline): Sensors.availability + SensorDevices.actionStatus
 *  - cycle N/M: SensorConfigs/<key>
 *  - interior temp/humidity + firmware: SensorData/status/<key>/recent/value, decoded via
 *    the sensor type's status rows (coded ids → names)
 *
 * Snapshot (SQL REST, re-read whenever a run/cycle changes):
 *  - outdoor ambient temp/humidity/pressure + latest analytical-run time: /v4/status
 *  - wind: v3 query (one-shot per sensor)
 */
@Component({
   selector: 'app-all-sensors-board',
   templateUrl: './all-sensors-board.component.html',
   styleUrls: ['./all-sensors-board.component.scss'],
   providers: [ZoneService],
})
export class AllSensorsBoardComponent implements OnInit, OnDestroy {
   sensors: BoardSensor[] = [];
   isLoading = true;
   isError = false;
   isStaff = false;

   searchText = '';
   staffFilter: StaffFilter = 'active';
   companyFilter = '';
   sortKey: SortKey = 'name';

   readonly sortOptions: { key: SortKey; label: string; staffOnly?: boolean }[] = [
      { key: 'name',          label: 'Name (A–Z)' },
      { key: 'name-desc',     label: 'Name (Z–A)' },
      { key: 'ping-newest',   label: 'Last message: newest' },
      { key: 'ping-oldest',   label: 'Last message: oldest' },
      { key: 'live-first',    label: 'Live first' },
      { key: 'offline-first', label: 'Offline first' },
      { key: 'running-first', label: 'Running first' },
      { key: 'stopped-first', label: 'Stopped first' },
      { key: 'company',       label: 'Company (A–Z)', staffOnly: true },
   ];

   readonly statusLabels = STATUS_LABELS;
   readonly statusIcons = STATUS_ICONS;

   private customerId: string | null = null;
   private baseSub?: Subscription;
   private readonly rowByKey = new Map<string, BoardSensor>();
   private readonly configSubByKey = new Map<string, Subscription>();
   private readonly statusDataSubByKey = new Map<string, Subscription>();
   private readonly windLoaded = new Set<string>();
   /**
    * Sensors whose wind query returned an error. The v5 endpoint 500s for some
    * keys; without this we would re-issue the same failing request on every
    * ambient refresh. Cleared only by a page load.
    */
   private readonly windFailed = new Set<string>();
   /**
    * Sensors whose run advanced since the last wind read. Wind belongs to a run,
    * so only these need refetching, not the whole fleet.
    */
   private readonly windDirty = new Set<string>();
   private readonly typeFields = new Map<string, TypeFields>();
   private ambientReloadTimer?: any;
   private ambientLoadedOnce = false;

   /**
    * False until the first /v4/status snapshot lands. The SQL call is slow
    * (seconds), and until it returns we must not assert "unknown"/"N/A" — that
    * reads as missing data rather than not-yet-loaded.
    */
   ambientReady = false;
   private windIdleHandle?: number;

   /** Bumped on any row-data mutation; drives the filteredSensors() memo. */
   private dataVersion = 0;
   private memoKey = '';
   private memoResult: BoardSensor[] = [];

   constructor(
      private _httpService: HttpService,
      private _authService: AuthService,
      private _zoneService: ZoneService,
   ) {}

   ngOnInit(): void {
      this.isStaff = !!this._authService.isUserStaff;
      // Everyone starts on Active; staff can widen to All/Inactive.
      this.staffFilter = 'active';
      this.customerId = this.isStaff
         ? null
         : (this._authService.objCustomerUser?.['info']?.['customerId'] || null);

      // Fail CLOSED: a non-staff user without a resolved tenant id must see
      // nothing. Falling through would issue an unscoped read and expose every
      // company's sensors.
      if (!this.isStaff && !this.customerId) {
         console.error('all-sensors-board: no customerId for a non-staff user — refusing to load.');
         this.isLoading = false;
         this.isError = true;
         return;
      }

      // Resolve the coded status-field ids per sensor type, then start the live list.
      this._httpService.getAsList(environment.APIS.SENSORTYPES, 1).pipe(take(1)).subscribe({
         next: (types: any[]) => {
            // A throw here would escape to RxJS's unhandled-error path (not the
            // error handler below), leaving the board stuck on "Loading…".
            try {
               this.buildTypeFields(types);
            } catch (e) {
               console.error('all-sensors-board: sensor-type decode failed', e);
            }
            this.startBaseSubscription();
         },
         error: () => {
            // Types unavailable — still show the list; interior decode just stays empty.
            this.startBaseSubscription();
         },
      });
   }

   ngOnDestroy(): void {
      this.baseSub?.unsubscribe();
      this.configSubByKey.forEach(s => s.unsubscribe());
      this.statusDataSubByKey.forEach(s => s.unsubscribe());
      if (this.ambientReloadTimer) clearTimeout(this.ambientReloadTimer);
      const cic = (window as any).cancelIdleCallback;
      if (typeof cic === 'function' && this.windIdleHandle != null) cic(this.windIdleHandle);
   }

   /* ── Sensor-type field resolution ───────────────────── */
   private buildTypeFields(types: any[]): void {
      for (const t of types || []) {
         if (!t?.key) continue;
         // RTDB returns an object (not an array) when a numerically-keyed list
         // has gaps, so coerce before iterating.
         const raw = t?.status?.rows;
         const rows: any[] = Array.isArray(raw) ? raw : Object.values(raw ?? {});
         const f: TypeFields = {};
         for (const r of rows) {
            const name = String(r?.name || '').toLowerCase();
            // Verified against live dev data: rows are named "Sensor Interior
            // Temperature" / "Sensor Interior Humidity" / "Total VOC". Also accept
            // "enclosure" wording, which other sensor types use for the same reading.
            if (!f.tempId && /(interior|enclosure)\s*temp/.test(name)) f.tempId = r.id;
            else if (!f.humId && /(interior|enclosure)\s*humid/.test(name)) f.humId = r.id;
            else if (!f.fwId && FIRMWARE_RE.test(name) && !FIRMWARE_EXCLUDE_RE.test(name)) f.fwId = r.id;
         }
         this.typeFields.set(t.key, f);
      }
   }

   /* ── Live list (Sensors + SensorDevices) ────────────── */
   private startBaseSubscription(): void {
      const sensors$ = this.isStaff || !this.customerId
         ? this._httpService.getAsList(environment.APIS.SENSORS)
         : this._httpService.getListByOrder(environment.APIS.SENSORS, 'customerId', this.customerId);
      const devices$ = this._httpService.getAsList(environment.APIS.SENSORDEVICES);

      this.isLoading = true;
      this.isError = false;
      this.baseSub = combineLatest([sensors$, devices$]).subscribe({
         next: ([rawSensors, rawDevices]: [SensorSchema[], any[]]) => {
            this.reconcile(rawSensors || [], rawDevices || []);
            // Staff land on the 'all' filter, so inactive rows are visible from
            // the first paint and need their interior listener attached now.
            this.ensureVisibleSubs();
            this.ensureWindForVisible();
            this.isLoading = false;
            if (!this.ambientLoadedOnce) {
               this.ambientLoadedOnce = true;
               this.reloadAmbient();
            }
         },
         error: err => {
            console.error('all-sensors-board: base load failed', err);
            this.isLoading = false;
            this.isError = true;
         },
      });
   }

   /** Merge the latest sensor/device snapshot into persistent rows. */
   private reconcile(rawSensors: SensorSchema[], rawDevices: any[]): void {
      const deviceByKey = new Map<string, any>();
      for (const d of rawDevices) if (d?.key) deviceByKey.set(d.key, d);

      const seen = new Set<string>();
      for (const s of rawSensors) {
         if (!s || !s.key) continue;
         seen.add(s.key);
         const existing = this.rowByKey.get(s.key);
         if (existing) {
            this.applyBaseFields(existing, s, deviceByKey.get(s.key));
            existing.searchBlob = this.buildSearchBlob(existing);
         } else {
            const row = this.newRow(s, deviceByKey.get(s.key));
            this.rowByKey.set(s.key, row);
            this.setupPerSensorSubs(row);
         }
      }

      // Drop sensors that disappeared.
      for (const key of Array.from(this.rowByKey.keys())) {
         if (seen.has(key)) continue;
         this.rowByKey.delete(key);
         this.configSubByKey.get(key)?.unsubscribe();
         this.configSubByKey.delete(key);
         this.statusDataSubByKey.get(key)?.unsubscribe();
         this.statusDataSubByKey.delete(key);
         this.windLoaded.delete(key);
      }

      this.sensors = Array.from(this.rowByKey.values());
      this.dataVersion++;
   }

   private newRow(s: SensorSchema, device?: any): BoardSensor {
      const row: BoardSensor = {
         key: s.key,
         name: s.name || 'Unnamed',
         address: s.address || '',
         customerName: s.customerName || '',
         customerId: s.customerId || '',
         sensorTypeId: s.sensorTypeId || '',
         timezone: s.timezone || '',
         isActive: !!s.isActive,
         statusCategory: 'offline',
         freshnessTier: 'cold',
         timeElapsed: '',
         lastReportUtc: '',
         lastReportLocal: '',
         minutesElapsed: -1,
         cycle: null,
         wind: null,
         interior: null,
         vitals: { ambientTemp: null, ambientHumidity: null, ambientPressure: null },
         searchBlob: '',
      };
      this.applyBaseFields(row, s, device);
      row.searchBlob = this.buildSearchBlob(row);
      return row;
   }

   /** Fields derived from the live Sensors + SensorDevices nodes. */
   private applyBaseFields(row: BoardSensor, s: SensorSchema, device?: any): void {
      const actionStatusNum = device?.actionStatus !== undefined && device?.actionStatus !== null
         ? Number(device.actionStatus)
         : NaN;
      const isOnline = s?.availability === global_variables.deviceStatus[0];
      const isRunning = isOnline && actionStatusNum === 2;

      row.name = s.name || 'Unnamed';
      row.address = s.address || '';
      row.customerName = s.customerName || '';
      row.customerId = s.customerId || '';
      row.sensorTypeId = s.sensorTypeId || '';
      row.timezone = s.timezone || '';
      row.isActive = !!s.isActive;
      row.statusCategory = !isOnline ? 'offline' : (isRunning ? 'running' : 'online');
      // Cycle only matters while running — subscribe SensorConfigs only then.
      this.reconcileConfigSub(row);
   }

   /* ── Per-sensor live subscriptions ──────────────────── */
   private setupPerSensorSubs(row: BoardSensor): void {
      // Decommissioned sensors are hidden from customers and hidden from staff
      // unless they switch the filter, so don't hold a live listener for them.
      // ensureVisibleSubs() attaches one on demand.
      if (row.isActive) this.subscribeStatusData(row);
      // Wind is fetched per visible row instead, see ensureWindForVisible().
   }

   /**
    * Fetch wind only for rows the current filter actually reveals. Each sensor is
    * still latched to one request, so this bounds the cost to what is on screen
    * rather than the whole fleet.
    */
   private ensureWindForVisible(): void {
      for (const row of this.filteredSensors()) this.loadWindFor(row);
   }

   /** Attach interior listeners for rows the current filter actually reveals. */
   private ensureVisibleSubs(): void {
      if (!this.isStaff || this.staffFilter === 'active') return;
      this.rowByKey.forEach(row => {
         if (!row.isActive && !this.statusDataSubByKey.has(row.key)) this.subscribeStatusData(row);
      });
   }

   /** Attach the SensorConfigs listener only while a sensor is running. */
   private reconcileConfigSub(row: BoardSensor): void {
      const running = row.statusCategory === 'running';
      const has = this.configSubByKey.has(row.key);
      if (running && !has) {
         this.subscribeConfig(row);
      } else if (!running && has) {
         this.configSubByKey.get(row.key)!.unsubscribe();
         this.configSubByKey.delete(row.key);
         if (row.cycle) { row.cycle = null; this.dataVersion++; }
      }
   }

   /** Live SensorConfigs → cycle N/M; a changed cycle re-reads SQL ambient. */
   private subscribeConfig(row: BoardSensor): void {
      const sub = this._httpService
         .getAsObject(`${environment.APIS.SENSORCONFIGS}/${row.key}`)
         .subscribe({
            next: (raw: any) => {
               const cycle = this.extractCycle(raw);
               const prevSig = row.cycle ? `${row.cycle.current}/${row.cycle.total}` : '';
               row.cycle = cycle;
               row.searchBlob = this.buildSearchBlob(row);
               this.dataVersion++;
               const newSig = cycle ? `${cycle.current}/${cycle.total}` : '';
               if (newSig !== prevSig) {
                  // This sensor started a new run, so its wind reading is stale.
                  // Other sensors' wind is unaffected by this sensor's cycle.
                  this.windDirty.add(row.key);
                  this.scheduleAmbientReload();
               }
            },
            error: () => { /* leave cycle as-is */ },
         });
      this.configSubByKey.set(row.key, sub);
   }

   /** Live interior telemetry decoded from the type's coded field ids. */
   private subscribeStatusData(row: BoardSensor): void {
      const sub = this._httpService
         .getAsObject(`${environment.APIS.SENSORDATA}/status/${row.key}/recent/value`)
         .subscribe({
            next: (v: any) => {
               row.interior = this.decodeInterior(v, row.sensorTypeId);
               row.searchBlob = this.buildSearchBlob(row);
               this.dataVersion++;
            },
            error: () => {
               row.interior = null;
               row.searchBlob = this.buildSearchBlob(row);
               this.dataVersion++;
            },
         });
      this.statusDataSubByKey.set(row.key, sub);
   }

   private decodeInterior(v: any, typeId: string): Interior | null {
      if (!v || typeof v !== 'object') return null;
      const f = this.typeFields.get(typeId) || {};
      const rawFw = f.fwId ? v[f.fwId] : null;
      const firmware = rawFw != null && String(rawFw).trim() && String(rawFw).trim().toUpperCase() !== 'N/A'
         ? String(rawFw).trim()
         : null;
      return {
         temp: f.tempId ? this.numOrNull(v[f.tempId]) : null,
         humidity: f.humId ? this.numOrNull(v[f.humId]) : null,
         firmware,
      };
   }

   /* ── Ambient re-read from SQL (debounced) ───────────── */
   private scheduleAmbientReload(): void {
      if (this.ambientReloadTimer) clearTimeout(this.ambientReloadTimer);
      this.ambientReloadTimer = setTimeout(() => this.reloadAmbient(), AMBIENT_RELOAD_DEBOUNCE_MS);
   }

   private reloadAmbient(): void {
      this._zoneService.getQueryStatusData(environment.SystemType).pipe(take(1)).subscribe({
         next: (res: any) => {
            const list: SensorsDashboardData[] = (res && res['sensors']) || [];
            const byKey = new Map<string, SensorsDashboardData>();
            for (const s of list) if (s?.sensorId) byKey.set(s.sensorId, s);
            this.rowByKey.forEach(row => {
               const status = byKey.get(row.key);
               this.applyAmbient(row, status);
               row.searchBlob = this.buildSearchBlob(row);
            });
            this.ambientReady = true;
            this.dataVersion++;
            // Refresh wind only for sensors whose run actually advanced. Sweeping
            // every row here cost one request per sensor each time any single
            // sensor changed cycle, and most of them render nothing.
            this.windDirty.forEach(key => {
               const row = this.rowByKey.get(key);
               if (!row) return;
               this.windLoaded.delete(key);
               this.loadWindFor(row);
            });
            this.windDirty.clear();
            // Rows revealed by the filter still need their first read.
            this.ensureWindForVisible();
         },
         error: err => {
            // Stop showing the pending placeholder — N/A is the honest answer now.
            this.ambientReady = true;
            this.dataVersion++;
            console.error('all-sensors-board: ambient reload failed', err);
         },
      });
   }

   private applyAmbient(row: BoardSensor, status?: SensorsDashboardData): void {
      // No status row at all means the sensor has never produced analytical
      // data — that is "cold", not "0 minutes ago".
      const minutes = status?.timeElapsed ? this.extractMinutes(status.timeElapsed) : -1;
      const m = status?.measurements;
      row.timeElapsed = status?.timeElapsed || '';
      row.lastReportUtc = status?.lastReportUtc || '';
      row.lastReportLocal = this.resolveLastReportLocal(status, row.timezone);
      row.minutesElapsed = minutes;
      row.freshnessTier =
         minutes < 0 ? 'cold' :
         minutes < FRESHNESS_FRESH_MAX_MIN ? 'fresh' :
         minutes < FRESHNESS_WARM_MAX_MIN ? 'warm' : 'cold';
      row.vitals = {
         ambientTemp: m?.ambient?.temperature ?? null,
         ambientHumidity: m?.ambient?.humidity ?? null,
         ambientPressure: m?.ambient?.pressure ?? null,
      };
   }

   /* ── Wind (one-shot per sensor) ─────────────────────── */
   private loadWindFor(row: BoardSensor): void {
      if (!row.customerId || this.windLoaded.has(row.key) || this.windFailed.has(row.key)) return;
      this.windLoaded.add(row.key);
      row.wind = { speed: null, direction: null, runTimeLocal: null, loading: true };

      const now = Math.floor(Date.now() / 1000);
      const past = now - 30 * 24 * 3600;
      const params: any = { timestampStart: past.toString(), timestampEnd: now.toString(), page: null, pageSize: 1 };

      this._zoneService.getQueryData([row.key], row.customerId, params).pipe(take(1)).subscribe({
         next: (res: any) => {
            const w = this.extractWeather(res);
            row.wind = { speed: w.speed, direction: w.direction, runTimeLocal: w.runTimeLocal };
            row.searchBlob = this.buildSearchBlob(row);
            this.dataVersion++;
         },
         error: () => {
            this.windFailed.add(row.key);
            row.wind = { speed: null, direction: null, runTimeLocal: null };
            row.searchBlob = this.buildSearchBlob(row);
            this.dataVersion++;
         },
      });
   }

   private extractWeather(res: any): { speed: number | null; direction: number | null; runTimeLocal: string | null } {
      const empty = { speed: null, direction: null, runTimeLocal: null };
      if (!res) return empty;
      const rec = res.data && Array.isArray(res.data) && res.data.length ? res.data[0] : res;
      if (!rec || typeof rec !== 'object') return empty;
      return {
         speed: this.numOrNull(rec.wind_speed ?? rec.wind_avg ?? rec.wind?.speed),
         direction: this.numOrNull(rec.wind_direction ?? rec.wind?.direction),
         runTimeLocal: (rec.automation_start_time_local_sensor && String(rec.automation_start_time_local_sensor)) || null,
      };
   }

   /* ── Filtering / sorting ────────────────────────────── */
   filteredSensors(): BoardSensor[] {
      // Called on every change-detection pass; recompute only when data or a
      // control actually changed, otherwise return the cached array.
      const key = [
         this.dataVersion, this.searchText, this.staffFilter,
         this.companyFilter, this.sortKey, this.isStaff,
      ].join('|');
      if (key === this.memoKey) return this.memoResult;
      this.memoKey = key;

      const q = (this.searchText || '').toLowerCase().trim();
      const company = this.companyFilter || '';
      const activeMode: StaffFilter = this.isStaff ? this.staffFilter : 'active';

      this.memoResult = this.sensors
         .filter(s => {
            if (activeMode === 'active' && !s.isActive) return false;
            if (activeMode === 'inactive' && s.isActive) return false;
            if (company && s.customerName !== company) return false;
            if (!q) return true;
            return s.searchBlob.includes(q);
         })
         .sort((a, b) => this.compare(a, b));
      return this.memoResult;
   }

   sortLabel(): string {
      return this.sortOptions.find(o => o.key === this.sortKey)?.label || 'Sort';
   }

   setSort(key: SortKey): void {
      this.sortKey = key;
   }

   private compare(a: BoardSensor, b: BoardSensor): number {
      const byName = a.name.localeCompare(b.name);
      switch (this.sortKey) {
         case 'name-desc':
            return -byName;
         case 'company':
            return a.customerName.localeCompare(b.customerName) || byName;
         case 'ping-newest': {
            const ka = a.minutesElapsed < 0 ? Number.POSITIVE_INFINITY : a.minutesElapsed;
            const kb = b.minutesElapsed < 0 ? Number.POSITIVE_INFINITY : b.minutesElapsed;
            return (ka - kb) || byName;
         }
         case 'ping-oldest': {
            const ka = a.minutesElapsed < 0 ? Number.NEGATIVE_INFINITY : a.minutesElapsed;
            const kb = b.minutesElapsed < 0 ? Number.NEGATIVE_INFINITY : b.minutesElapsed;
            return (kb - ka) || byName;
         }
         case 'live-first':
            return (this.liveRank(b) - this.liveRank(a)) || byName;
         case 'offline-first':
            return (this.liveRank(a) - this.liveRank(b)) || byName;
         case 'running-first':
            return (this.runRank(b) - this.runRank(a)) || byName;
         case 'stopped-first':
            return (this.runRank(a) - this.runRank(b)) || byName;
         case 'name':
         default:
            return byName;
      }
   }

   private liveRank(s: BoardSensor): number { return s.statusCategory === 'offline' ? 0 : 1; }
   private runRank(s: BoardSensor): number { return s.statusCategory === 'running' ? 1 : 0; }

   /* ── Interaction ────────────────────────────────────── */
   companies(): string[] {
      const set = new Set<string>();
      for (const s of this.sensors) if (s.customerName) set.add(s.customerName);
      return Array.from(set).sort((a, b) => a.localeCompare(b));
   }

   setStaffFilter(mode: StaffFilter): void {
      this.staffFilter = mode;
      this.ensureVisibleSubs();
      this.ensureWindForVisible();
   }
   /** Controls that change which rows are visible must top up their wind reads. */
   onVisibleSetChanged(): void {
      this.ensureWindForVisible();
   }

   trackBySensor(_: number, s: BoardSensor): string { return s.key; }
   /* Route for one sensor's live page. Bound as a routerLink on the row so the
      anchor gets a real href — the row can be opened in a new tab. */
   sensorLink(sensor: BoardSensor): any[] { return ['/sensor', sensor.key, 'live']; }

   hasWind(s: BoardSensor): boolean {
      return !!(s.wind && !s.wind.loading && (s.wind.direction != null || s.wind.speed != null));
   }

   windText(s: BoardSensor): string {
      const w = s.wind;
      if (!w) return '';
      const dir = w.direction != null ? this.compassFromDegrees(w.direction) : '';
      const spd = w.speed != null ? `${w.speed.toFixed(1)} mph` : '';
      return [dir, spd].filter(Boolean).join(' · ');
   }

   /* ── Hover tooltips (sensor timezone) ───────────────── */
   pingTooltip(s: BoardSensor): string {
      const t = this.fmtSensorTime(s.lastReportUtc, s.timezone);
      return t ? `Last report: ${t}` : 'No reports available.';
   }


   firmwareTooltip(s: BoardSensor): string {
      return s.interior?.firmware ? `Firmware ${s.interior.firmware}` : 'Firmware';
   }

   windTooltip(s: BoardSensor): string {
      // Only ever attribute the wind value to its OWN run time. Falling back to
      // lastReportUtc would stamp a possibly-weeks-old reading with a fresh time.
      const run = s.wind?.runTimeLocal;
      return run ? `Wind reading for run: ${run}` : 'Wind reading — run time unavailable';
   }

   /**
    * Timestamp of the latest run carrying analytical data, in the sensor's own
    * timezone. Prefer the API's pre-rendered lastReportLocal so this page and the
    * Sensors Dashboard agree; format lastReportUtc ourselves only as a fallback.
    */
   private resolveLastReportLocal(status: SensorsDashboardData | undefined, tz: string): string {
      const fromApi = (status?.lastReportLocal || '').trim();
      if (fromApi) return fromApi;
      return this.fmtSensorTime(status?.lastReportUtc || '', tz);
   }

   private fmtSensorTime(utc: string, tz: string): string {
      if (!utc) return '';
      const m = tz ? moment.utc(utc).tz(tz) : moment.utc(utc);
      if (!m || !m.isValid()) return utc;
      return m.format('MMM D, YYYY h:mm A z');
   }

   private compassFromDegrees(deg: number | null | undefined): string {
      if (deg == null || isNaN(deg)) return '';
      const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
      const idx = Math.round(((deg % 360) / 22.5)) % 16;
      return dirs[(idx + 16) % 16];
   }

   private extractCycle(raw: any): Cycle | null {
      if (!raw || typeof raw !== 'object') return null;
      const cfg = raw['Current_Modal_Type'] && raw[raw['Current_Modal_Type']]
         ? raw[raw['Current_Modal_Type']]
         : raw;
      const current = this.intOrNull(cfg['runNumber']);
      const currentType = cfg['Current_Type'];
      const typeCfg = currentType && cfg[currentType] ? cfg[currentType] : null;
      const total = typeCfg ? this.intOrNull(typeCfg['Num_of_Cycle']) : null;
      const percent = this.intOrNull(cfg['runProgressPercent']);
      if (current == null && total == null) return null;
      return { current: current ?? 0, total: total ?? 0, percent };
   }

   private buildSearchBlob(s: BoardSensor): string {
      const cycle = s.cycle ? `cycle ${s.cycle.current} / ${s.cycle.total}` : '';
      const wind = this.hasWind(s) ? `wind ${this.windText(s)}` : '';
      const interior = s.interior
         ? `interior ${s.interior.temp ?? ''} ${s.interior.humidity ?? ''} ${s.interior.firmware ?? ''}`
         : '';
      return [
         s.name, s.address, s.customerName, s.key,
         STATUS_LABELS[s.statusCategory],
         s.isActive ? 'active' : 'inactive',
         s.timeElapsed, s.lastReportUtc, s.lastReportLocal,
         cycle, wind, interior,
         s.vitals.ambientTemp != null ? `${s.vitals.ambientTemp}` : '',
         s.vitals.ambientHumidity != null ? `${s.vitals.ambientHumidity}` : '',
         s.vitals.ambientPressure != null ? `${s.vitals.ambientPressure}` : '',
      ].join(' ').toLowerCase();
   }

   private extractMinutes(timeElapsed: string): number {
      // -1 means "unknown" so the row lands in the 'cold' tier and sinks in the
      // ping sorts. Returning 0 would paint an unreported sensor as freshest.
      if (!timeElapsed) return -1;
      let minutes = 0;
      const d = timeElapsed.match(/(\d+)\s*days?/);
      const h = timeElapsed.match(/(\d+)\s*hrs?/);
      const m = timeElapsed.match(/(\d+)\s*mins?/);
      if (d) minutes += parseInt(d[1], 10) * 1440;
      if (h) minutes += parseInt(h[1], 10) * 60;
      if (m) minutes += parseInt(m[1], 10);
      // No recognised unit (e.g. "2 months", "45 seconds") — treat as unknown.
      if (!d && !h && !m) return -1;
      return minutes;
   }

   private intOrNull(v: any): number | null {
      if (v === null || v === undefined || v === '') return null;
      const n = parseInt(v, 10);
      return isNaN(n) ? null : n;
   }

   private numOrNull(v: any): number | null {
      if (typeof v === 'number' && !isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '') {
         const n = parseFloat(v);
         return isNaN(n) ? null : n;
      }
      return null;
   }
}
